import React, { useState, useMemo } from 'react';
import './EventTable.css';

const EventTable = ({ events, onEdit, onDelete, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [sortField, setSortField] = useState('event_title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Get unique categories and regions for filters
  const categories = useMemo(() => {
    const cats = [...new Set(events.map(e => e.categoryPrimary || e.category || 'Uncategorized'))];
    return cats.sort();
  }, [events]);

  const regions = useMemo(() => {
    const regs = [...new Set(events.map(e => e.region || 'DC'))];
    return regs.sort();
  }, [events]);

  // Filter and sort events
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events.filter(event => {
      const matchesSearch = !searchTerm || 
        (event.event_title || event.eventTitle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.venue || event.venueName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event['location/address'] || event.address || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !filterCategory || 
        (event.categoryPrimary || event.category || 'Uncategorized') === filterCategory;
      
      const matchesRegion = !filterRegion || 
        (event.region || 'DC') === filterRegion;
      
      return matchesSearch && matchesCategory && matchesRegion;
    });

    // Sort events
    filtered.sort((a, b) => {
      const aValue = a[sortField] || '';
      const bValue = b[sortField] || '';
      
      if (sortDirection === 'asc') {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });

    return filtered;
  }, [events, searchTerm, filterCategory, filterRegion, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedEvents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEvents = filteredAndSortedEvents.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCategory('');
    setFilterRegion('');
    setCurrentPage(1);
  };

  return (
    <div className="event-table-container">
      {/* Header */}
      <div className="table-header">
        <div className="table-title">
          <h2>📅 Events Management</h2>
          <p>{filteredAndSortedEvents.length} events found</p>
        </div>
        <div className="table-actions">
          <button onClick={onRefresh} className="btn-secondary">
            🔄 Refresh
          </button>
          <button onClick={clearFilters} className="btn-secondary">
            🧹 Clear Filters
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="table-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <select
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            className="filter-select"
          >
            <option value="">All Regions</option>
            {regions.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table className="event-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('event_title')} className="sortable">
                Event Name {sortField === 'event_title' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('venue')} className="sortable">
                Venue {sortField === 'venue' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('categoryPrimary')} className="sortable">
                Category {sortField === 'categoryPrimary' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('region')} className="sortable">
                Region {sortField === 'region' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('start_time')} className="sortable">
                Time {sortField === 'start_time' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedEvents.map((event) => (
              <tr key={event.id}>
                <td className="event-name">
                  <div className="event-title">
                    {event.event_title || event.eventTitle || 'Untitled Event'}
                  </div>
                  <div className="event-description">
                    {(event.description || '').substring(0, 100)}
                    {(event.description || '').length > 100 && '...'}
                  </div>
                </td>
                <td className="venue-cell">
                  <div className="venue-name">
                    {event.venue || event.venueName || 'Unknown Venue'}
                  </div>
                  <div className="venue-address">
                    {event['location/address'] || event.address || 'No address'}
                  </div>
                </td>
                <td className="category-cell">
                  <span className="category-tag">
                    {event.categoryPrimary || event.category || 'Uncategorized'}
                  </span>
                </td>
                <td className="region-cell">
                  <span className={`region-badge ${event.region || 'DC'}`}>
                    {event.region || 'DC'}
                  </span>
                </td>
                <td className="time-cell">
                  {event.start_time || event.startTime || 'TBD'}
                </td>
                <td className="actions-cell">
                  <button
                    onClick={() => onEdit(event)}
                    className="action-btn edit-btn"
                    title="Edit event"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => onDelete(event.id)}
                    className="action-btn delete-btn"
                    title="Delete event"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ← Previous
          </button>
          
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next →
          </button>
        </div>
      )}

      {/* Empty State */}
      {filteredAndSortedEvents.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No events found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default EventTable;
